/**
 * User settings types for theme and locale preferences
 */

/**
 * Theme preference options
 */
export type Theme = 'light' | 'dark' | 'system'

/**
 * Supported locales
 */
export type SupportedLocale = 'en' | 'pl'

/**
 * Notification preferences
 */
export interface NotificationPreferences {
  emailNotifications: boolean
  pushNotifications: boolean
}

/**
 * Password change request
 */
export interface PasswordChangeRequest {
  currentPassword: string
  newPassword: string
}

/**
 * User settings object
 */
export interface UserSettings {
  theme: Theme
  locale: SupportedLocale
  notifications?: NotificationPreferences
}

/**
 * Cookie names for cross-subdomain persistence
 * Cookies are used instead of localStorage because localStorage
 * is not shared between different subdomains (localhost vs app.localhost)
 */
export const THEME_COOKIE_NAME = 'app-theme'
export const LOCALE_COOKIE_NAME = 'app-locale'

/**
 * Legacy localStorage keys for backward compatibility during migration
 */
export const LEGACY_THEME_STORAGE_KEY = 'theme-preference'

/**
 * Default settings for new users
 */
export const DEFAULT_SETTINGS: UserSettings = {
  theme: 'system',
  locale: 'en',
}

/**
 * Check if we're on localhost (development)
 */
export function isLocalhost(): boolean {
  const hostname = window.location.hostname
  return hostname === 'localhost' || hostname.endsWith('.localhost')
}

/**
 * Check if we're in production (not localhost)
 * In production, cookies with domain attribute work for cross-subdomain sharing
 */
export function isProduction(): boolean {
  return !isLocalhost()
}

/**
 * Get the root domain for cookie sharing
 * Returns undefined for localhost (browsers handle localhost cookies specially)
 * Returns root domain for production (e.g., 'example.com' from 'app.example.com')
 */
function getCookieDomain(): string | undefined {
  const hostname = window.location.hostname

  // For localhost development, don't set domain attribute
  // Browsers handle localhost cookies inconsistently with domain attribute
  if (isLocalhost()) {
    return undefined
  }

  // For production, extract root domain (e.g., 'example.com' from 'app.example.com')
  const parts = hostname.split('.')
  if (parts.length >= 2) {
    return parts.slice(-2).join('.')
  }
  return hostname
}

/**
 * Set a cookie with cross-subdomain support
 * For localhost: uses localStorage as fallback for cross-subdomain sharing
 */
export function setCookie(name: string, value: string, days = 365): void {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString()
  const domain = getCookieDomain()

  // Build cookie string
  let cookieStr = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
  if (domain) {
    cookieStr += `; domain=${domain}`
  }
  document.cookie = cookieStr

  // For localhost, also save to localStorage as backup for cross-subdomain sharing
  // This is because cookies don't share well across localhost subdomains
  if (isLocalhost()) {
    try {
      localStorage.setItem(name, value)
    } catch {
      // Ignore localStorage errors
    }
  }
}

/**
 * Get a cookie value
 * For localhost: also checks localStorage as fallback for cross-subdomain sharing
 */
export function getCookie(name: string): string | null {
  // Try reading from cookie first
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=')
    if (cookieName === name && cookieValue) {
      return decodeURIComponent(cookieValue)
    }
  }

  // For localhost, check localStorage as fallback
  // This enables cross-subdomain sharing (localhost <-> app.localhost)
  if (isLocalhost()) {
    try {
      const stored = localStorage.getItem(name)
      if (stored) {
        return stored
      }
    } catch {
      // Ignore localStorage errors
    }
  }

  return null
}

/**
 * Read theme from cookie with fallback to legacy localStorage
 */
export function getStoredTheme(): Theme | null {
  // Try cookie first (preferred for cross-subdomain)
  const cookieTheme = getCookie(THEME_COOKIE_NAME)
  if (cookieTheme && ['light', 'dark', 'system'].includes(cookieTheme)) {
    return cookieTheme as Theme
  }

  // Fallback to legacy localStorage for migration
  const legacyTheme = localStorage.getItem(LEGACY_THEME_STORAGE_KEY)
  if (legacyTheme && ['light', 'dark', 'system'].includes(legacyTheme)) {
    // Migrate to cookie
    setCookie(THEME_COOKIE_NAME, legacyTheme)
    return legacyTheme as Theme
  }

  return null
}

/**
 * Store theme preference in cookie
 */
export function setStoredTheme(theme: Theme): void {
  setCookie(THEME_COOKIE_NAME, theme)
}

/**
 * Read locale from cookie
 */
export function getStoredLocale(): SupportedLocale | null {
  const cookieLocale = getCookie(LOCALE_COOKIE_NAME)
  if (cookieLocale && ['en', 'pl'].includes(cookieLocale)) {
    return cookieLocale as SupportedLocale
  }
  return null
}

/**
 * Store locale preference in cookie
 */
export function setStoredLocale(locale: SupportedLocale): void {
  setCookie(LOCALE_COOKIE_NAME, locale)
}
